import { APIAccessor, HttpError, ListResponse } from '../utils/api';
import { formatSize } from '../utils/formatting';
import { BaseModel } from './base';
import { UserModel } from './user';

const IMAGE_EXTENSIONS = new Set(['bmp', 'gif', 'jpeg', 'jpg', 'png', 'webp']);

export class FileModel extends BaseModel {
    public id = '';
    public name = '';
    public size = 0;
    public owner = '';
    public thumbnail_url?: string;
    public download_url = '';
    public direct_url = '';
    public view_url = '';

    // eslint-disable-next-line unicorn/consistent-class-member-order -- conflicts with @typescript-eslint/member-ordering, which requires instance fields before static methods
    public static async getById(id: string, apiAccessor: APIAccessor): Promise<FileModel | undefined> {
        try {
            const api = await apiAccessor.fetch(`/api/v1/files/${encodeURIComponent(id)}`);
            return this.wrapNew(api);
        } catch (error) {
            if (error instanceof HttpError && (error.status === 404 || error.status === 403)) {
                return undefined;
            }

            throw error;
        }
    }

    public static async getByUser(user: UserModel, apiAccessor: APIAccessor): Promise<FileModel[]> {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        const response = (await apiAccessor.fetch(
            `/api/v1/users/${encodeURIComponent(user.id)}/files`,
        )) as ListResponse;
        return response.items.map((index) => this.wrapNew(index));
    }

    public static wrapNew(object: unknown): FileModel {
        const file = new FileModel();
        return file.wrap(object);
    }

    public async delete(apiAccessor: APIAccessor): Promise<void> {
        await apiAccessor.fetch(`/api/v1/files/${encodeURIComponent(this.id)}`, {
            method: 'DELETE',
        });
    }

    public async rename(name: string, apiAccessor: APIAccessor): Promise<void> {
        await apiAccessor.fetch(`/api/v1/files/${encodeURIComponent(this.id)}`, {
            method: 'PATCH',
            data: { name },
        });

        this.name = name;
    }

    public getExtension(): string {
        return this.name.split('.').at(-1)?.toLowerCase() ?? '';
    }

    public isImage(): boolean {
        return IMAGE_EXTENSIONS.has(this.getExtension());
    }

    public getFormattedSize(): string {
        return formatSize(this.size);
    }
}
